import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { DataImportReferenceData, IDataImportService, TemplateType } from "services/DataImportService";
import { RootState } from "setup";
import { SERVICE_TYPES } from "setup/dependency/ServiceTypes";
import { container } from "setup/dependency/inversify.config";
import { toWorksheetTypeEntry, WorksheetTypeEntry } from "./sheetMappings";
import { TEMPLATE_TYPE_IDS } from "../shared/templateData";

// Types API reference lists (mapping dropdowns, enum validation). Session-stable data shared
// across wizard steps, so unlike the step slices this one deliberately does NOT reset when
// the active submission changes.

type DataImportTypesState = {
  ReferenceData: DataImportReferenceData | null;
  LoadingReferenceData: boolean;
  ReferenceDataLoadFailed: boolean;
  // Worksheet types reference list per template (the BE endpoint requires the numeric template
  // type filter and returns an empty list without it). Session-stable like ReferenceData.
  WorksheetTypes: Partial<Record<TemplateType, WorksheetTypeEntry[]>>;
  LoadingWorksheetTypes: boolean;
};

const initialState: DataImportTypesState = {
  ReferenceData: null,
  LoadingReferenceData: false,
  ReferenceDataLoadFailed: false,
  WorksheetTypes: {},
  LoadingWorksheetTypes: false,
};

export const referenceDataSlice = createSlice({
  name: "dataImportTypes",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(getReferenceData.pending, (state) => {
        state.LoadingReferenceData = true;
        state.ReferenceDataLoadFailed = false;
      })
      .addCase(getReferenceData.fulfilled, (state, action) => {
        state.ReferenceData = action.payload;
        state.LoadingReferenceData = false;
      })
      .addCase(getReferenceData.rejected, (state) => {
        state.LoadingReferenceData = false;
        state.ReferenceDataLoadFailed = true;
      })
      .addCase(getWorksheetTypes.pending, (state) => {
        state.LoadingWorksheetTypes = true;
      })
      .addCase(getWorksheetTypes.fulfilled, (state, action) => {
        state.WorksheetTypes[action.meta.arg.templateType] = action.payload;
        state.LoadingWorksheetTypes = false;
      })
      .addCase(getWorksheetTypes.rejected, (state) => {
        state.LoadingWorksheetTypes = false;
      });
  },
});

// Reference lists are stable for the session — refetch only after a failure or when forced.
export const getReferenceData = createAsyncThunk<DataImportReferenceData, { force?: boolean } | undefined>(
  "DataImport/GetReferenceData",
  async () => {
    const dataImportService = container.get<IDataImportService>(SERVICE_TYPES.DataImportService);
    return await dataImportService.getReferenceData();
  },
  {
    condition: (arg, { getState }) => {
      const state = getState() as RootState;
      if (state.dataImportTypes.LoadingReferenceData) return false;
      return arg?.force === true || !state.dataImportTypes.ReferenceData;
    },
  }
);

// Worksheet types are stable for the session — refetch only after a failure.
export const getWorksheetTypes = createAsyncThunk(
  "DataImport/GetWorksheetTypes",
  async (arg: { templateType: TemplateType }) => {
    const dataImportService = container.get<IDataImportService>(SERVICE_TYPES.DataImportService);
    const worksheetTypes = await dataImportService.getWorksheetTypes(TEMPLATE_TYPE_IDS[arg.templateType]);
    return worksheetTypes.map(toWorksheetTypeEntry);
  },
  {
    condition: (arg, { getState }) => {
      const state = getState() as RootState;
      return !state.dataImportTypes.WorksheetTypes[arg.templateType] && !state.dataImportTypes.LoadingWorksheetTypes;
    },
  }
);

export const selectWorksheetTypes = (state: RootState) => state.dataImportTypes.WorksheetTypes;
export const selectReferenceData = (state: RootState) => state.dataImportTypes.ReferenceData;
export const selectReferenceDataLoadFailed = (state: RootState) => state.dataImportTypes.ReferenceDataLoadFailed;

export default referenceDataSlice.reducer;
